import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useAuth } from "../../../../shared/auth/AuthProvider";
import { reviewAdminApi as api, reviewError, thaiReviewDate, reviewAvatarUrl, reviewAuthorName } from "./courseReviewAdminApi";
import AdminReviewCourseFilter from "./AdminReviewCourseFilter";

const PAGE_SIZE = 10;
const ACTIONS = { suspend: "ระงับเผยแพร่", restore: "เปิดเผยแพร่", delete: "ลบ" };
const textStyle = { whiteSpace: "pre-wrap", overflowWrap: "anywhere" };

export function ReviewAuthor({ review }) {
  const name = reviewAuthorName(review.authorDisplayName);
  const url = reviewAvatarUrl(review.authorAvatarUrl);
  const [failedUrl, setFailedUrl] = useState(null);
  const initial = Array.from(name).find((character) => /\p{L}/u.test(character)) || "U";
  return <div className="d-flex align-items-center gap-2">
    {url && failedUrl !== url ? <img src={url} alt="" width="40" height="40" className="rounded-circle" style={{ objectFit: "cover" }} referrerPolicy="no-referrer" onError={() => setFailedUrl(url)} />
      : <span aria-hidden="true" className="rounded-circle bg-light border d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 40, height: 40 }}>{initial}</span>}
    <span style={{ overflowWrap: "anywhere" }}>{name || "ผู้เรียนคอร์ส"}</span>
  </div>;
}

export default function AdminCourseReviews() {
  const { user, userDoc, loading } = useAuth();
  if (loading) return <div role="status">กำลังตรวจสอบสิทธิ์...</div>;
  if (!user?.uid || userDoc?.role !== "admin") return <Alert variant="warning">หน้านี้สำหรับผู้ดูแลระบบเท่านั้น</Alert>;
  return <ReviewManagement key={user.uid} />;
}

function History({ reviewId, onHide }) {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const mounted = useRef(true);
  const load = async (next = null) => {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError("");
    try {
      const result = await api.history({ reviewId, pageSize: PAGE_SIZE, ...(next ? { cursor: next } : {}) });
      if (mounted.current) { setEvents((previous) => next ? [...previous, ...result.events] : result.events); setCursor(result.nextCursor); }
    } catch (failure) { if (mounted.current) setError(reviewError(failure)); }
    finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  useEffect(() => { mounted.current = true; load(); return () => { mounted.current = false; }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <Modal show onHide={onHide} centered scrollable>
    <Modal.Header closeButton><Modal.Title>ประวัติการจัดการรีวิว</Modal.Title></Modal.Header>
    <Modal.Body>
      {busy && <div role="status">กำลังโหลด...</div>}
      {error && <Alert variant="danger">{error} <Button onClick={() => load(cursor)} disabled={busy}>ลองอีกครั้ง</Button></Alert>}
      {!busy && !error && !events.length && <p>ยังไม่มีประวัติการจัดการ</p>}
      {events.map((event, index) => <p key={`${event.revision}-${index}`}>{ACTIONS[event.action] || "อัปเดตรีวิว"} · {thaiReviewDate(event.createdAt)} · รุ่น {event.revision}</p>)}
      {cursor && <Button variant="outline-primary" disabled={busy} onClick={() => load(cursor)}>โหลดประวัติเพิ่มเติม</Button>}
    </Modal.Body>
    <Modal.Footer><Button variant="secondary" onClick={onHide}>ปิด</Button></Modal.Footer>
  </Modal>;
}

function ReviewManagement() {
  const [status, setStatus] = useState("");
  const [course, setCourse] = useState("");
  const [reference, setReference] = useState("");
  const [selection, setSelection] = useState({});
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const pending = useRef(false);
  const requestId = useRef(0);
  const actionTrigger = useRef(null);
  const refreshButton = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; requestId.current += 1; }; }, []);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true); setError(""); setItems([]); setNextCursor(null);
    const payload = selection.reviewId ? { reviewId: selection.reviewId }
      : { ...selection, pageSize: PAGE_SIZE, ...(cursor ? { cursor } : {}) };
    api.list(payload).then((result) => {
      if (id === requestId.current) { setItems(result.reviews); setNextCursor(result.nextCursor); }
    }).catch((failure) => { if (id === requestId.current) setError(reviewError(failure)); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
  }, [selection, cursor, reload]);

  const choose = (next) => { setSelection(next); setCursor(null); setNotice(""); };
  const openAction = (action, review, trigger) => { actionTrigger.current = trigger; setDialog({ action, review }); setReason(""); setActionError(""); };
  const refreshReview = async (reviewId) => {
    const result = await api.list({ reviewId });
    if (!mounted.current) return;
    const current = result.reviews[0];
    const matches = current && (!selection.status || current.status === selection.status) && (!selection.courseId || current.courseId === selection.courseId);
    setItems((previous) => previous.flatMap((item) => item.reviewId === reviewId ? (matches ? [current] : []) : [item]));
  };
  const mutate = async () => {
    if (pending.current || !dialog) return;
    const { action, review } = dialog;
    const cleanReason = reason.trim();
    if (action === "suspend" && (!cleanReason || Array.from(reason).length > 300 || /[\p{Cc}\p{Cf}]/u.test(reason))) {
      setActionError("กรุณาระบุเหตุผล 1–300 ตัวอักษร โดยไม่ขึ้นบรรทัดใหม่"); return;
    }
    pending.current = true; setBusy(true); setActionError(""); setNotice(""); setError("");
    let committed = false;
    try {
      await api[action]({ reviewId: review.reviewId, expectedRevision: review.revision,
        ...(action === "suspend" ? { reason: cleanReason } : {}), ...(action === "delete" ? { confirmDelete: true } : {}) });
      committed = true;
      if (action === "delete") {
        if (mounted.current) setItems((previous) => previous.filter((item) => item.reviewId !== review.reviewId));
      } else await refreshReview(review.reviewId);
      if (mounted.current) { setDialog(null); setNotice(`${ACTIONS[action]}สำเร็จ`); }
    } catch (failure) {
      if (!mounted.current) return;
      const conflict = /(?:^|\/)aborted$/.test(failure?.code || "");
      if (conflict || committed || /(?:^|\/)not-found$/.test(failure?.code || "")) {
        setDialog(null);
        // Hide stale actionable data if a canonical refresh cannot complete.
        try { await refreshReview(review.reviewId); }
        catch { if (mounted.current) setItems((previous) => previous.filter((item) => item.reviewId !== review.reviewId)); }
        if (mounted.current) setError(committed ? "บันทึกสำเร็จ แต่โหลดข้อมูลล่าสุดไม่สำเร็จ กรุณาโหลดรายการใหม่" : reviewError(failure));
      } else setActionError(reviewError(failure));
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };

  return <div>
    <h4>จัดการรีวิว</h4><p className="text-muted">ตรวจสอบและจัดการรีวิวคอร์สเรียน</p>
    <Form onSubmit={(event) => { event.preventDefault(); choose({ ...(status ? { status } : {}), ...(course.trim() ? { courseId: course.trim() } : {}) }); }}>
      <fieldset disabled={busy || loading}>
        <Row className="g-2 align-items-end mb-3">
          <Col md={4}><Form.Group controlId="review-status"><Form.Label>สถานะ</Form.Label><Form.Select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">ทั้งหมด</option><option value="published">เผยแพร่</option><option value="suspended">ระงับเผยแพร่</option>
          </Form.Select></Form.Group></Col>
          <Col md={5}><AdminReviewCourseFilter value={course} onChange={setCourse} disabled={busy || loading} /></Col>
          <Col md={3}><Button type="submit">กรองรายการ</Button></Col>
        </Row>
      </fieldset>
    </Form>
    <Form className="mb-3" onSubmit={(event) => { event.preventDefault(); if (/^[a-f0-9]{64}$/.test(reference.trim())) choose({ reviewId: reference.trim() }); }}>
      <Form.Group controlId="review-reference"><Form.Label>ค้นหาเลขอ้างอิงรีวิว</Form.Label><div className="d-flex gap-2">
        <Form.Control value={reference} onChange={(event) => setReference(event.target.value)} pattern="[a-f0-9]{64}" required maxLength={64} disabled={busy || loading} />
        <Button type="submit" disabled={busy || loading || !/^[a-f0-9]{64}$/.test(reference.trim())}>ค้นหา</Button>
      </div></Form.Group>
    </Form>
    {selection.reviewId && <p>ผลค้นหาเลขอ้างอิง <Button size="sm" variant="outline-secondary" disabled={busy || loading} onClick={() => { setStatus(""); setCourse(""); choose({}); }}>กลับรายการทั้งหมด</Button></p>}
    {notice && <Alert variant="success" role="status">{notice}</Alert>}
    {error && <Alert variant="danger">{error} <Button disabled={busy || loading} onClick={() => setReload((previous) => previous + 1)}>ลองอีกครั้ง</Button></Alert>}
    {loading && <div role="status"><Spinner size="sm" animation="border" /> กำลังโหลดรีวิว...</div>}
    {!loading && !error && !items.length && <p>ไม่พบรีวิว</p>}
    {items.map((review) => <Card className="mb-3" key={review.reviewId}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-2"><ReviewAuthor review={review} /><Badge bg={review.status === "published" ? "success" : "secondary"}>{review.status === "published" ? "เผยแพร่" : "ระงับเผยแพร่"}</Badge></div>
        <div className="small text-muted mt-2">วันที่รีวิว {thaiReviewDate(review.createdAt)}</div>
        {review.updatedAt !== review.createdAt && <div className="small text-muted">แก้ไขล่าสุด {thaiReviewDate(review.updatedAt)}</div>}
        <p className="mt-3" style={textStyle}>{review.reviewText}</p>
        <p className="small text-muted" style={{ overflowWrap: "anywhere" }}>{review.courseName}</p>
        <Form.Group controlId={`reference-${review.reviewId}`} className="mb-3"><Form.Label className="small">เลขอ้างอิงรีวิว</Form.Label><Form.Control size="sm" readOnly value={review.reviewId} onFocus={(event) => event.target.select()} /></Form.Group>
        <div className="d-flex gap-2 flex-wrap">
          {review.status === "published" && <Button variant="outline-warning" disabled={busy || loading} onClick={(event) => openAction("suspend", review, event.currentTarget)}>ระงับเผยแพร่</Button>}
          {review.status === "suspended" && <Button variant="outline-success" disabled={busy || loading} onClick={(event) => openAction("restore", review, event.currentTarget)}>เปิดเผยแพร่</Button>}
          <Button variant="outline-danger" disabled={busy || loading} onClick={(event) => openAction("delete", review, event.currentTarget)}>ลบ</Button>
          <Button variant="outline-secondary" disabled={busy || loading} onClick={() => setHistory(review.reviewId)}>ประวัติ</Button>
        </div>
      </Card.Body>
    </Card>)}
    <div className="d-flex gap-2">
      <Button ref={refreshButton} variant="outline-secondary" disabled={busy || loading} onClick={() => { setCursor(null); setReload((previous) => previous + 1); }}>โหลดรายการใหม่</Button>
      {cursor && <Button variant="outline-secondary" disabled={busy || loading} onClick={() => setCursor(null)}>หน้าแรก</Button>}
      {nextCursor && <Button disabled={busy || loading} onClick={() => setCursor(nextCursor)}>หน้าถัดไป</Button>}
    </div>
    <Modal show={!!dialog} onHide={() => { if (!pending.current) setDialog(null); }} onExited={() => { if (!actionTrigger.current?.isConnected) refreshButton.current?.focus(); }} centered scrollable backdrop={busy ? "static" : true} keyboard={!busy}>
      <Modal.Header closeButton={!busy}><Modal.Title>{dialog ? ACTIONS[dialog.action] : ""}รีวิว</Modal.Title></Modal.Header>
      <Modal.Body>
        {dialog?.action === "delete" ? <p>รีวิวจะถูกลบถาวร และไม่สามารถกู้คืนเนื้อหาผ่านระบบรีวิวได้ ระบบจะเก็บข้อมูลขั้นต่ำเพื่อป้องกันการส่งรีวิวใหม่อัตโนมัติสำหรับคอร์สเดิม การลบนี้ไม่ลบข้อมูลคอร์ส การลงทะเบียน การชำระเงิน โปรไฟล์ หรือรูปภาพ</p> : <p>ยืนยันการ{dialog ? ACTIONS[dialog.action] : ""}รีวิวนี้?</p>}
        {dialog?.action === "suspend" && <Form.Group controlId="suspend-reason"><Form.Label>เหตุผล (1–300 ตัวอักษร)</Form.Label><Form.Control autoFocus value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} /></Form.Group>}
        {actionError && <Alert variant="danger" className="mt-3">{actionError}</Alert>}
        {busy && <div role="status">กำลังบันทึก...</div>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setDialog(null)}>ยกเลิก</Button><Button variant={dialog?.action === "delete" ? "danger" : "primary"} disabled={busy} onClick={mutate}>ยืนยัน</Button></Modal.Footer>
    </Modal>
    {history && <History key={history} reviewId={history} onHide={() => setHistory(null)} />}
  </div>;
}
